'use client';

import React, { useMemo } from 'react';
import NearByStoresSection from './NearByStoresSection';
import ProductsCarousel from '@/components/carousel/products-carousel';
import ImagesCarousel from '@/components/carousel/ImagesCarousel';
import { Section } from '../services/types';
import { HomeScreenSectionTypeEnum } from '@/lib/utils/enums';
import { BasicProductDataResponse } from '@/types/catalog';
import DownloadApp from '@/components/DownloadApp';
import Banner from './Banner';
import { DestinationValue } from '@/types/cms';
import TrendingStoresSection from './TrendingStores';
import DigitalCard from '../../digital-cards/components/DigitalCardLink';
import Container from '@/components/ui/container';

type TrenderList = {
  sections: Section[];
};

const CmsList = ({ sections }: TrenderList) => {
  const validSections = useMemo(() => {
    return sections.filter((section) => {
      const parsedContent = tryParseContent(section.content);
      return parsedContent && Array.isArray(parsedContent)
        ? parsedContent.length > 0
        : !!parsedContent;
    });
  }, [sections]);

  return (
    <>
      <article>
        <Container className="mt-6 flex flex-col gap-12 overflow-hidden pt-4 text-center lg:gap-20">
          {validSections.map((section) => {
            const sectionContent = renderSectionContent(section);
            return sectionContent ? <div key={section.id}>{sectionContent}</div> : null;
          })}
          {Boolean(validSections?.length) ? <DownloadApp /> : null}
        </Container>
      </article>
    </>
  );
};

export default CmsList;

function tryParseContent(content: string) {
  try {
    return JSON.parse(content);
  } catch (e) {
    return null;
  }
}

function renderSectionContent(section: Section) {
  const parsedContent = JSON.parse(section.content);

  if (!parsedContent || parsedContent.length === 0) {
    return null; // Return null if there's no content to render
  }

  switch (section.sectionsType) {
    case HomeScreenSectionTypeEnum.Slider:
      return (
        <Banner
          slides={
            parsedContent as {
              ArabicImage: string;
              EnglishImage: string;
              DestinationValue: DestinationValue;
              Destination: number;
            }[]
          }
        />
      );
    case HomeScreenSectionTypeEnum.NearbyList:
      return (
        <NearByStoresSection
          slides={parsedContent}
          englishName={section.name ?? ''}
          arabicName={section.name ?? ''}
        />
      );
    case HomeScreenSectionTypeEnum.PromotedStore:
      const storeProducts = parsedContent as BasicProductDataResponse[];
      const productsList = storeProducts.map((item) => ({
        ...item,
        DefaultImage: item.defaultImage,
      }));
      return (
        <ProductsCarousel
          englishName={section.name ?? ''}
          arabicName={section.name ?? ''}
          slides={productsList}
        />
      );
    case HomeScreenSectionTypeEnum.FourImages:
      return (
        <ImagesCarousel
          description={section.description}
          name={section.name}
          showingSectionDescription={section.showingSectionDescription}
          showingSectionTitle={section.showingSectionTitle}
          numberOfImages="fourImages"
          sliders={parsedContent}
        />
      );
    case HomeScreenSectionTypeEnum.TwoImages:
      return (
        <ImagesCarousel
          description={section.description}
          name={section.name}
          showingSectionDescription={section.showingSectionDescription}
          showingSectionTitle={section.showingSectionTitle}
          numberOfImages="twoImages"
          sliders={parsedContent}
        />
      );
    case HomeScreenSectionTypeEnum.ThreeImages:
      return (
        <ImagesCarousel
          description={section.description}
          name={section.name}
          showingSectionDescription={section.showingSectionDescription}
          showingSectionTitle={section.showingSectionTitle}
          numberOfImages="threeImages"
          sliders={parsedContent}
        />
      );
    case HomeScreenSectionTypeEnum.OneImage:
      return (
        <ImagesCarousel
          description={section.description}
          name={section.name}
          showingSectionDescription={section.showingSectionDescription}
          showingSectionTitle={section.showingSectionTitle}
          numberOfImages="oneImage"
          sliders={parsedContent}
        />
      );
    case HomeScreenSectionTypeEnum.TrendingStores:
      return <TrendingStoresSection section={section.content} description={section.name} />;

    case HomeScreenSectionTypeEnum.DigitalCards:
      return <DigitalCard data={section} />;
    default:
      return null;
  }
}
